import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ShieldCheck, Lock, Gamepad2, FileText } from "lucide-react";

const TITLE = "Security & Data Access Rules - Inquira";
const DESC =
  "How Inquira protects creator payout configuration and player game state: the intent, access rules, and write paths behind our affiliate_settings and user_game policies.";

export const Route = createFileRoute("/security")({
  component: SecurityDocsPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/security" }],
  }),
});

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-rule bg-card p-5 space-y-3">
      <h2 className="flex items-center gap-2 text-lg font-extrabold text-ink">
        <span className="text-neon-pink">{icon}</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-ink-muted">{children}</div>
    </section>
  );
}

function Rule({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 border-t border-rule pt-2 sm:flex-row sm:gap-4">
      <span className="w-44 shrink-0 text-xs font-bold uppercase tracking-wider text-ink">
        {label}
      </span>
      <span className="text-sm text-ink-muted">{value}</span>
    </div>
  );
}

function SecurityDocsPage() {
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto w-full max-w-3xl px-4 py-8 space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Inquira
        </Link>

        <header className="space-y-3">
          <h1 className="text-3xl font-extrabold text-ink">Security &amp; data access rules</h1>
          <p className="text-sm leading-relaxed text-ink-muted">
            This page is maintained by the Inquira team to answer common questions about how two
            sensitive parts of our database are locked down: creator payout configuration
            (<code className="text-ink">affiliate_settings</code>) and player game state
            (<code className="text-ink">user_game</code>). It describes controls we have enabled
            today. It is not a certification, audit report, or independent verification.
          </p>
          <p className="text-xs text-ink-muted">
            Shared responsibility: our hosting platform provides the managed database, row-level
            security enforcement, and authentication primitives. Inquira is responsible for the
            policies and write paths described below. You are responsible for keeping your own
            account credentials private.
          </p>
        </header>

        <Section icon={<Lock className="h-5 w-5" />} title="affiliate_settings — payout configuration">
          <p>
            <strong className="text-ink">Intent.</strong> This is a single configuration record that
            controls money: the creator commission rate, the minimum payout balance, payout windows,
            and cookie/attribution lifetimes. Anyone able to read or change it could infer or alter
            how commissions are calculated, so it is treated as administrative configuration rather
            than app content.
          </p>
          <Rule label="Who can read it" value="Super admins only, through an admin-only routine that verifies the caller's role on the server." />
          <Rule label="Who can change it" value="Super admins only, through an admin-only update routine that records the change." />
          <Rule label="Direct app writes" value="Denied for every signed-in and signed-out visitor by a restrictive policy, with no exceptions." />
          <Rule label="Public read access" value="Intentionally absent. Commission economics are not published, so there is no public read rule and none is needed." />
          <Rule label="Why 'no public read' is by design" value="Scanners flag a missing public read rule as a possible gap. Here the absence is the control: the page-level and dashboard-level features that need these values run server-side as an admin." />
        </Section>

        <Section icon={<Gamepad2 className="h-5 w-5" />} title="user_game — XP, quests and rewards">
          <p>
            <strong className="text-ink">Intent.</strong> This record holds a player's XP, level,
            coins, quest progress, and chest rewards. Because these values unlock cosmetic rewards
            and leaderboard standing, they must only ever be produced by our own game logic — never
            by a value a client sends up.
          </p>
          <Rule label="Who can read it" value="Each signed-in user can read their own game record. No one can read another player's record." />
          <Rule label="Who can create it" value="Nobody directly. The record is created for you the first time our server-side game routine loads your state." />
          <Rule label="Who can change it" value="Only server-side game routines: earning XP, completing quests, and claiming chests all run as trusted database functions that re-derive the reward before writing." />
          <Rule label="Direct app writes" value="Denied by a restrictive policy for insert, update, and delete." />
          <Rule label="Why 'no insert rule' is by design" value="A missing insert rule is fail-closed on purpose. Adding one would let a client mint its own XP or coin balance, which is exactly the abuse this design prevents." />
        </Section>

        <Section icon={<ShieldCheck className="h-5 w-5" />} title="How we keep these rules from drifting">
          <p>
            Every database change is reviewed by an automated gate before a release can be
            published. The gate blocks publishing when any security finding is still unresolved, when
            a change would make an internal routine callable by signed-out visitors, or when a
            write-deny rule on a privileged table is removed without a replacement.
          </p>
          <p>
            Changes to our internal security notes — including any accepted risk like the two
            fail-closed patterns above — are recorded in an append-only change log with the reason
            for the change, visible to super admins.
          </p>
        </Section>

        <Section icon={<FileText className="h-5 w-5" />} title="Reporting a security issue">
          <p>
            Email{" "}
            <a className="font-bold text-neon-pink hover:underline" href="mailto:info@inquira.online">
              info@inquira.online
            </a>{" "}
            with steps to reproduce. Please do not access or modify data that is not yours while
            testing. See our{" "}
            <Link to="/privacy" className="font-bold text-neon-pink hover:underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="font-bold text-neon-pink hover:underline">
              Terms
            </Link>
            .
          </p>
        </Section>
      </main>
    </div>
  );
}
